import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, Sparkles, AlertTriangle, ClockAlert, BookX,
  Users, ReceiptText, Wallet, MessageCircle, ShieldQuestion, Mail,
  PlayCircle, Check, Star, Phone, Building2, ChevronDown, BarChart3,
  TrendingUp, BellRing, Briefcase, Smartphone, Menu, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import AuthModal from "../components/AuthModal";
import WhatsAppFloat from "../components/WhatsAppFloat";
import DistributorSignupModal from "../components/DistributorSignupModal";
import GooglePlayBadge, { PLAY_STORE_URL } from "../components/GooglePlayBadge";
import DemoVideoModal from "../components/DemoVideoModal";
import usePlatformWa, { buildWaLink } from "../lib/usePlatformWa";
import { api } from "../lib/api";
import Logo from "../components/Logo";

const PROBLEMS = [
  { icon: ClockAlert, title: "Payment late aata hai", body: "Customers waqt par paisa nahi detay. Aap ka cash flow phans jata hai." },
  { icon: BookX, title: "Hisaab manage karna mushkil", body: "Copy-pencil pe hisaab? Numbers gum ho jate hain, customer disputes badhte hain." },
  { icon: AlertTriangle, title: "Recovery weak hai", body: "Kis ko follow up karna hai? Kal yaad nahi rahega. Recovery rate gir jata hai." },
];

const FEATURES = [
  { icon: Users, title: "Customer tracking", body: "Har customer ka complete history — loans, payments, calls, defaulter risk — sab ek jagah." },
  { icon: ReceiptText, title: "Installment management", body: "Mobile, motorcycle, fridge — kuch bhi qist par bechain. Schedule khud-ba-khud ban jata hai." },
  { icon: Wallet, title: "Payment tracking", body: "Cash, bank, JazzCash, Easypaisa — har payment record ho jata hai. Late fee bhi auto." },
  { icon: MessageCircle, title: "WhatsApp reminders", body: "Click-to-WhatsApp se ek tap mein customer ko reminder bhejein. Apne hi number se." },
  { icon: ShieldQuestion, title: "Risk check", body: "Naya customer? Pehle defaulter network check karein. Bad debt se bachein." },
  { icon: BarChart3, title: "Reports & insights", body: "Daily collections, overdue list, top defaulters — ek nazar mein sab kuch." },
];

const STEPS = [
  { n: 1, title: "Customer add karein", body: "Naam, mobile aur CNIC. 30 second mein new customer profile ban jata hai." },
  { n: 2, title: "Installment set karein", body: "Item, sale price, down payment, qist plan. Schedule auto-generate ho jata hai." },
  { n: 3, title: "Payment track + reminder", body: "Har installment record karein. WhatsApp se reminder bhejein. Hisaab strong." },
];

const TESTIMONIALS = [
  { name: "Imran Bhai", shop: "Star Mobile · Lahore", body: "Pehle copy mein hisaab rakhta tha — half time rakam bhool jata tha. Ab har customer ka exact balance dikhta hai. Recovery 30% behtar hui hai." },
  { name: "Saima Apa", shop: "Saima Electronics · Karachi", body: "WhatsApp reminders ne meri zindagi asaan kar di. Pehle din bhar phone karti thi, ab ek tap mein 20 customers ko reminder chala jata hai." },
  { name: "Rashid Khan", shop: "Khan Bikes · Faisalabad", body: "Risk check feature ne mujhe 3 baar chone se bachaya. Naya customer aata hai, pehle network check, phir loan." },
];

const UNIFIED_FEATURES = [
  "Unlimited Customers",
  "Unlimited Loans",
  "Digital Agreements",
  "Recovery Calendar",
  "WhatsApp Reminders",
  "Customer Ledger",
  "Receipts",
  "Blacklist Management",
  "Risk Score",
  "Salesman Tracking",
  "Android App Access",
  "All Current & Future Core Features",
];

const PLANS = [
  {
    id: "monthly",
    name: "Monthly Plan",
    price: "1,500",
    cycle: "/ month",
    tag: "🔥 Most Popular",
    badge: "popular",
    subline: "PKR 1,500 / Month · Sab features included",
    features: UNIFIED_FEATURES,
    highlight: true,
  },
  {
    id: "yearly",
    name: "Yearly Plan",
    price: "15,000",
    cycle: "/ year",
    tag: "💰 Save PKR 3,000",
    badge: "best_value",
    subline: "PKR 15,000 / Year · 2 months free vs monthly · Priority support",
    features: [...UNIFIED_FEATURES, "Priority Support"],
    highlight: false,
  },
];

const FAQS = [
  { q: "Subscription kitne ka hai?", a: "Monthly Plan PKR 1,500/month, Yearly Plan PKR 15,000/year. Koi hidden fee nahi — WhatsApp verification ke baad account activate ho jata hai." },
  { q: "Internet na ho to chalega?", a: "App online hai. Aap ki shop par WiFi ya 4G chahiye. Lekin har action turant cloud par save ho jata hai, data kabhi nahi gum hota." },
  { q: "WhatsApp reminders ke liye API chahiye?", a: "Nahi. Aap ke apne WhatsApp Business number se direct deep-link chalta hai. Koi API, token ya monthly fee nahi — bas connect karein aur use karein." },
  { q: "Apna data delete kar sakte hain?", a: "Haan. Settings se aap apna account delete kar saktay hain, data 30 din mein purge ho jata hai." },
];

export default function Landing() {
  const nav = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [demoOpen, setDemoOpen] = useState(false);
  const [distOpen, setDistOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // mobile hamburger
  const platformWa = usePlatformWa();
  const supportHref = buildWaLink(platformWa.support_number, platformWa.support_message);

  useEffect(() => {
    // Track ?ref= click so partners see Total Link Clicks in their dashboard.
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      const key = `hp_ref_click_${ref}_${new Date().toISOString().slice(0, 10)}`;
      if (!sessionStorage.getItem(key)) {
        api.post("/referrals/track-click", { code: ref.toUpperCase().trim() })
          .catch((err) => console.warn("referral click track failed:", err?.message || err));
        try {
          sessionStorage.setItem(key, "1");
        } catch (storageErr) {
          // sessionStorage can be unavailable in incognito/private mode — that's
          // expected; we silently drop the dedupe key on this device.
          console.debug("sessionStorage unavailable, click dedupe skipped:", storageErr?.message || storageErr);
        }
      }
    }
  }, []);

  const openSignup = () => { setAuthMode("signup"); setAuthOpen(true); };
  const openLogin = () => { setAuthMode("login"); setAuthOpen(true); };

  const onAuthSuccess = (u) => {
    if (u.role === "platform_admin") nav("/admin");
    else if (u.role === "distributor") nav("/distributor");
    else nav("/");
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="landing-nav-logo">
            <Logo size={32} priority />
            <div className="font-display font-bold text-xl tracking-tight">HisaabPro</div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <button type="button" onClick={() => setDistOpen(true)} className="hover:text-slate-900" data-testid="landing-nav-distributor-btn">Refer &amp; Earn</button>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition"
              data-testid="landing-nav-download-app"
            >
              <Smartphone className="w-4 h-4" /> Download App
            </a>
            <Button variant="ghost" onClick={openLogin} className="hidden sm:inline-flex text-slate-700 hover:text-emerald-700" data-testid="landing-login-btn">Login</Button>
            <Button onClick={openSignup} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="landing-nav-signup-btn">
              Get Started
            </Button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="md:hidden ml-1 w-10 h-10 grid place-items-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
              aria-label="Open menu"
              data-testid="landing-mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="landing-mobile-menu">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <Logo size={28} />
                <div className="font-display font-bold text-lg">HisaabPro</div>
              </div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="w-10 h-10 grid place-items-center rounded-md hover:bg-slate-100"
                aria-label="Close menu"
                data-testid="landing-mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-6 text-slate-700 space-y-1">
              {[
                { href: "#features", label: "Features" },
                { href: "#how", label: "How it works" },
                { href: "#pricing", label: "Pricing" },
                { href: "#faq", label: "FAQ" },
              ].map((it) => (
                <a
                  key={it.href}
                  href={it.href}
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2.5 rounded-md hover:bg-slate-50 font-medium"
                >
                  {it.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setDistOpen(true); }}
                className="w-full text-left block px-3 py-2.5 rounded-md hover:bg-slate-50 font-medium"
                data-testid="landing-mobile-refer"
              >
                Refer &amp; Earn
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); openLogin(); }}
                className="w-full text-left block px-3 py-2.5 rounded-md hover:bg-slate-50 font-medium"
                data-testid="landing-mobile-login"
              >
                Login
              </button>
              <div className="pt-4 border-t border-slate-100 mt-4 space-y-3">
                <Button
                  onClick={() => { setMenuOpen(false); openSignup(); }}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="landing-mobile-signup"
                >
                  Get Started
                </Button>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 px-1">Android App</div>
                  <GooglePlayBadge size="md" testId="landing-mobile-play-badge" className="w-full justify-center" />
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-b from-emerald-50/60 via-white to-white" />
        <div aria-hidden className="absolute -z-10 top-20 right-[-10%] w-[480px] h-[480px] rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-16 sm:pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" /> Pakistan ka apna installment software
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Installment business ka <span className="text-emerald-600">poora control</span>, ab mobile mein.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-xl">
              Hisaab strong, recovery strong — ab koi payment miss nahi hoga. Customer add karein,
              installments set karein, WhatsApp se reminder bhejein. Sab ek hi screen par.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={openSignup} className="h-12 px-6 text-base bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="landing-hero-cta">
                Get Started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button onClick={() => setDemoOpen(true)} variant="outline" className="h-12 px-6 text-base border-slate-300" data-testid="landing-demo-btn">
                <PlayCircle className="w-5 h-5 mr-1.5 text-emerald-600" /> Watch Demo
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
              <GooglePlayBadge size="md" testId="landing-hero-play-badge" />
              <div className="text-sm text-slate-600">
                Ya <b>Android app</b> download karein — sab kuch mobile se manage.
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 pt-1">
              <Check className="w-4 h-4 text-emerald-600" />
              Sirf PKR 1,500 / month — koi card ya hidden fee nahi.
            </div>
          </div>
          <DashboardMock />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-bold text-rose-700">Roz ka masla</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Installment business mein 3 sabse bade dard</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {PROBLEMS.map((p) => (
              <div key={p.title} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`problem-${p.title}`}>
                <div className="w-10 h-10 rounded-md bg-rose-50 border border-rose-100 grid place-items-center text-rose-600">
                  <p.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-lg mt-4">{p.title}</div>
                <div className="text-slate-600 text-sm mt-1.5">{p.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION (Features) */}
      <section id="features" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">Solution</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Ek system, sab kaam aasaan.</h2>
            <p className="text-slate-600 mt-3">Manual hisaab khatam — HisaabPro ke saath aap ki shop digital ho jaye gi.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="group bg-white border border-slate-200 rounded-xl p-6 transition hover:shadow-md hover:-translate-y-0.5 hover:border-emerald-300" data-testid={`feature-${f.title}`}>
                <div className="w-11 h-11 rounded-md bg-emerald-50 border border-emerald-100 grid place-items-center text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white transition">
                  <f.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-lg mt-4">{f.title}</div>
                <div className="text-slate-600 text-sm mt-1.5 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-16 sm:py-20 bg-slate-900 text-white relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-bold text-emerald-400">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">3 step mein shuru karein</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur" data-testid={`step-${s.n}`}>
                <div className="font-display text-5xl font-bold text-emerald-400 tabular leading-none">0{s.n}</div>
                <div className="font-display font-bold text-lg mt-4">{s.title}</div>
                <div className="text-slate-300 text-sm mt-1.5 leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-bold">
              <Star className="w-3.5 h-3.5 fill-emerald-700 text-emerald-700" /> 100+ shops using HisaabPro
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Asli shop walay, asli baat.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col" data-testid={`testimonial-${t.name}`}>
                <div className="flex gap-0.5 text-amber-400">
                  {["s1","s2","s3","s4","s5"].map((s) => <Star key={s} className="w-4 h-4 fill-amber-400" />)}
                </div>
                <p className="text-slate-700 mt-4 leading-relaxed">{`"${t.body}"`}</p>
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.shop}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">Pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Saaf, simple pricing</h2>
            <p className="text-slate-600 mt-3">PKR 1,500 / month ya PKR 15,000 / year. Koi card nahi, koi hidden fee nahi.</p>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-600" data-testid="landing-pricing-android-note">
              <Smartphone className="w-3.5 h-3.5 text-emerald-700" />
              Available on Android —{" "}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                data-testid="landing-pricing-play-link"
              >
                Download from Google Play
              </a>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-10 items-stretch max-w-3xl mx-auto">
            {PLANS.map((p) => {
              const ringCls = p.badge === "popular"
                ? "border-emerald-500 bg-white shadow-xl ring-1 ring-emerald-200"
                : "border-sky-400 bg-white shadow-lg ring-1 ring-sky-200";
              const tagBg = p.badge === "popular"
                ? "bg-emerald-600 text-white"
                : "bg-sky-600 text-white";
              return (
                <div
                  key={p.id}
                  data-testid={`plan-${p.id}`}
                  className={`relative rounded-xl border p-6 flex flex-col ${ringCls}`}
                >
                  {p.tag && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${tagBg} text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap`}>
                      {p.tag}
                    </div>
                  )}
                  <div className="font-display text-2xl font-bold mt-2">{p.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-slate-500 text-sm">PKR</span>
                    <span className="font-display text-4xl font-bold tabular" data-testid={`plan-price-${p.id}`}>{p.price}</span>
                    <span className="text-slate-500 text-sm">{p.cycle}</span>
                  </div>
                  {p.subline && (
                    <div className="mt-1 text-xs font-semibold text-amber-800">{p.subline}</div>
                  )}
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={openSignup}
                    className={`mt-6 h-11 ${p.highlight ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                    data-testid={`plan-cta-${p.id}`}
                  >
                    Get Started
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REFERRAL PARTNER CALLOUT */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 sm:p-10 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-200/50 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-bold">
                <Users className="w-3.5 h-3.5" /> Refer &amp; Earn — Free to Join
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Refer shops, earn PKR 500 per shop</h2>
              <p className="text-slate-600 mt-3 max-w-xl mx-auto">
                Apna unique referral link ya code share karein — customer khud sign up karta hai.
                Jab woh subscription (Monthly PKR 1,500 ya Yearly PKR 15,000) pay karte hain,
                aap ko har qualifying payment par <b>PKR 500 fixed reward</b> milta hai.
              </p>
              <div className="mt-6 grid sm:grid-cols-4 gap-3 max-w-2xl mx-auto text-left" data-testid="landing-refer-journey">
                {[
                  { n: 1, t: "Share App", d: "Referral link / code / QR" },
                  { n: 2, t: "Customer Signs Up", d: "Website ya Android app par" },
                  { n: 3, t: "Subscription Pay", d: "PKR 1,500/month ya PKR 15,000/year" },
                  { n: 4, t: "Aap Earn Karein", d: "PKR 500 fixed reward har payment par" },
                ].map((s) => (
                  <div key={s.n} className="rounded-md bg-white border border-emerald-100 p-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">{s.n}</div>
                    <div className="mt-2 font-display font-bold text-sm">{s.t}</div>
                    <div className="text-xs text-slate-600">{s.d}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Button onClick={() => setDistOpen(true)} className="h-12 px-7 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="landing-dist-cta">
                  <Users className="w-4 h-4 mr-1.5" /> Become a Referral Partner
                </Button>
                <GooglePlayBadge size="md" testId="landing-refer-play-badge" />
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Free to join · Instant activation · Withdraw above PKR 500
                <div className="mt-1">Download HisaabPro on Google Play — customers khud sign up karain, aap earn karain.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO CONTENT — richer prose block to lift content-to-code ratio and rank for long-tail Qist queries */}
      <section className="py-14 sm:py-16 bg-white border-t border-slate-100" data-testid="landing-seo-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-5 text-slate-700 text-[15px] leading-7">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            Pakistan ka apna installment (Qist) management software
          </h2>
          <p>
            HisaabPro Pakistan ke shop owners ke liye specifically banaya gaya ek complete
            installment business management platform hai. Chahe aap Mobile Shop chalate hain,
            Electronics ya Furniture bechte hain, motorcycle ya home appliances Qist par de rahe
            hain — HisaabPro aap ki har customer, har guarantor, har loan aur har payment ka poora
            record ek jagah mehfooz rakhta hai. Aap ki team laptop, tablet ya Android phone se
            same real-time data dekh sakti hai.
          </p>
          <p>
            Pakistan mein installment business mein tin sab se bade dard hote hain: customer time
            par payment nahi karte, salesman ka commission calculate karna mushkil hota hai, aur
            recovery follow-up manual copy-registers mein ulajh jaati hai. HisaabPro in tino
            problems ka digital solution hai. WhatsApp reminders bina kisi paid API ke seedha aap
            ke apne number se jaate hain, Recovery Calendar aap ko har din ki priority list detta
            hai, aur Risk Score guarantor ki verify ki ja rahi information ke basis par 0–100 ka
            explainable score generate karta hai — taake aap loan approve karne se pehle sahi
            faisla karein.
          </p>
          <p>
            HisaabPro ki pricing seedhi hai — koi hidden fee nahi, koi setup charge nahi.{" "}
            <strong>Monthly Plan PKR 1,500 / month</strong> mein aap ko unlimited customers,
            unlimited loans, digital agreements, customer ledger, salesman tracking aur Android app
            access mil jaati hai. Agar aap ek saal ka plan lein to <strong>Yearly Plan
            PKR 15,000 / year</strong> par 2 months free milte hain plus Priority Support included
            hoti hai. Aap kisi bhi cycle par plan switch kar sakte hain.
          </p>
          <p>
            Data security aur privacy hamari top priority hai. Saari traffic HTTPS-encrypted hai,
            passwords bcrypt (cost factor 12) ke saath hash hote hain, 5 failed login attempts ke
            baad 15 minute ka automatic lockout hota hai, aur har shop ka data database level par
            tenant-isolated hai. Aap ke customers ki information (CNIC, mobile, photos) sirf aap ke
            shop ke authorised users hi dekh sakte hain — kisi bhi doosri shop ke user ko access
            nahi milta. Zyada tafseel <a href="/privacy-policy" className="text-emerald-700 font-semibold underline">Privacy Policy</a>{" "}
            mein maujood hai.
          </p>
          <p>
            Android app Google Play Store par available hai — <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold underline">yahan se download karein</a>{" "}
            — aur website version bhi same features offer karta hai. WhatsApp reminders,
            Blacklist check, Refer &amp; Earn program aur PDF agreement generation sab platforms
            par work karte hain. Sign up karein, WhatsApp verification complete karein, subscription
            pay karein aur sirf 5 minute mein apni pehli customer entry ready ho sakti hai.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center">Sawalat (FAQ)</h2>
          <div className="mt-10 space-y-2">
            {FAQS.map((f) => <FAQItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight" data-testid="landing-cta-heading">
            Manage your installment business from anywhere.
          </h2>
          <p className="text-emerald-50 mt-4 text-base sm:text-lg max-w-2xl mx-auto">
            Download the HisaabPro Android App today — sirf <b>PKR 1,500/month</b> ya <b>PKR 15,000/year</b>. Koi card, koi setup fee — sirf install karein aur shuru ho jayein.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-3" data-testid="landing-cta-buttons">
            <Button onClick={openSignup} className="h-12 px-7 text-base bg-white text-emerald-700 hover:bg-emerald-50" data-testid="landing-final-cta">
              Get Started (Website) <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <GooglePlayBadge size="lg" variant="light" testId="landing-final-play-badge" />
          </div>
          <div className="mt-6 flex justify-center">
            <a
              href={supportHref}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="h-10 px-5 inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-md border border-white/30 hover:bg-white/10 transition"
              data-testid="landing-final-wa"
            >
              <MessageCircle className="w-4 h-4" /> Chat with us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <Logo size={32} />
              <div className="font-display font-bold text-xl text-white tracking-tight">HisaabPro</div>
            </div>
            <p className="mt-3 text-sm max-w-xs">Pakistan ka apna installment management system.</p>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest font-bold text-white">Download Android App</div>
              <div className="mt-3">
                <GooglePlayBadge size="md" testId="landing-footer-play-badge" />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-white">Product</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              <li>
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1" data-testid="landing-footer-android-link">
                  <Smartphone className="w-3.5 h-3.5" /> Android App
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-white">Legal</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a href="/privacy-policy" className="hover:text-white" data-testid="landing-footer-privacy">Privacy Policy</a>
              </li>
              <li>
                <a href="/terms" className="hover:text-white" data-testid="landing-footer-terms">Terms &amp; Conditions</a>
              </li>
              <li>
                <a href="/data-deletion" className="hover:text-white" data-testid="landing-footer-deletion">Data Deletion</a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-white">Support</div>
            <a href={supportHref} target="_blank" rel="nofollow noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-semibold" data-testid="landing-footer-wa">
              <MessageCircle className="w-4 h-4" /> WhatsApp: {platformWa.support_display}
            </a>
            <div className="mt-3 text-sm flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Mon–Sat · 10am–8pm PKT</div>
            <a href="mailto:support@myhisaabpro.com" className="mt-3 text-xs text-slate-400 hover:text-white flex items-center gap-2" data-testid="landing-footer-email">
              <Mail className="w-3.5 h-3.5" /> support@myhisaabpro.com
            </a>
          </div>
        </div>
        <div className="border-t border-slate-900 py-5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center text-xs text-slate-500">
            © {new Date().getFullYear()} HisaabPro. All rights reserved.
          </div>
        </div>
      </footer>

      <WhatsAppFloat />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} mode={authMode} onSuccess={onAuthSuccess} />
      <DistributorSignupModal open={distOpen} onOpenChange={setDistOpen} />

      {/* Watch Demo — embedded YouTube modal, never leaves the site */}
      <DemoVideoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        videoId="R1zJqvfzUpY"
        title="HisaabPro — Demo Video"
      />
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`faq-item-${q.slice(0, 12)}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left p-4 sm:px-5 hover:bg-slate-50 transition"
      >
        <span className="font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 text-slate-600 text-sm leading-relaxed">{a}</div>}
    </div>
  );
}

function DashboardMock() {
  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-emerald-400/20 to-sky-400/20 blur-2xl" />
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[11px] font-mono text-slate-400">hisaabpro.app/dashboard</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Dashboard</div>
              <div className="font-display text-xl font-bold mt-0.5">Al-Madina Mobiles</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">This month</div>
              <div className="font-display text-xl font-bold tabular">PKR 2,84,500</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: "Active loans", value: "127", tone: "emerald", icon: ReceiptText },
              { label: "Overdue", value: "14", tone: "rose", icon: AlertTriangle },
              { label: "Recovery", value: "92%", tone: "amber", icon: TrendingUp },
            ].map((s) => {
              const t = {
                emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                rose: "bg-rose-50 border-rose-200 text-rose-700",
                amber: "bg-amber-50 border-amber-200 text-amber-700",
              }[s.tone];
              return (
                <div key={s.label} className="border border-slate-200 rounded-lg p-3">
                  <div className={`w-7 h-7 rounded-md grid place-items-center border ${t}`}><s.icon className="w-3.5 h-3.5" /></div>
                  <div className="font-display font-bold text-lg tabular mt-2 leading-tight">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
              <BellRing className="w-3 h-3" /> Recovery — Today
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { name: "Imran Khan", phone: "0300-XXX-1234", amount: "Rs 5,200", days: "9 days late", tone: "rose" },
                { name: "Saima Akhtar", phone: "0301-XXX-5678", amount: "Rs 3,000", days: "Due today", tone: "amber" },
                { name: "Zain Mehmood", phone: "0322-XXX-9988", amount: "Rs 7,500", days: "Tomorrow", tone: "emerald" },
              ].map((r) => {
                const t = {
                  rose: "bg-rose-50 text-rose-700 border-rose-200",
                  amber: "bg-amber-50 text-amber-700 border-amber-200",
                  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
                }[r.tone];
                return (
                  <div key={r.name} className="px-3 py-2.5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{r.name}</div>
                      <div className="text-[10px] text-slate-500 tabular">{r.phone}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${t}`}>{r.days}</span>
                      <div className="text-sm font-bold tabular">{r.amount}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
